import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Factory,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  ClipboardList,
  Star,
  Target,
  BarChart3,
  Building2,
  Utensils,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const CFIA_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-red-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-orange-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <Utensils size={18} className="text-red-400" />
            <span>Canadian Food Safety Standards</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400">
            CFIA Certification
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Canadian Food Inspection Agency - Ensuring Food Safety and
            Regulatory Compliance for the Canadian Market
          </p>
        </motion.div>
      </section>

      {/* What is CFIA */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is CFIA Compliance?
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p>
              CFIA compliance ensures that food products meet Canadian food
              safety and regulatory standards set by the Canadian Food
              Inspection Agency. This certification is essential for any food
              business seeking to export to Canada or operate within the
              Canadian food market.
            </p>
            <p>
              The Canadian Food Inspection Agency (CFIA) is responsible for food
              safety and compliance enforcement in Canada. CFIA compliance is
              mandatory for food manufacturers, processors, importers, and
              exporters wishing to access the Canadian market or export Canadian
              products internationally. Understanding CFIA requirements is
              crucial for maintaining food safety and regulatory compliance.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Key Features of CFIA Compliance
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Food Safety Standards",
                desc: "Ensures food products meet strict Canadian food safety requirements.",
                icon: ShieldCheck,
              },
              {
                title: "Hazard Analysis",
                desc: "Implements HACCP principles for identifying and controlling food safety risks.",
                icon: Target,
              },
              {
                title: "Product Labeling Requirements",
                desc: "Ensures proper labeling with nutrition facts, ingredients, and allergen information.",
                icon: FileCheck,
              },
              {
                title: "Facility Inspection & Registration",
                desc: "Requires facilities to meet inspection standards and register with CFIA.",
                icon: Building2,
              },
              {
                title: "Record Keeping & Traceability",
                desc: "Maintains detailed records for product traceability and recall management.",
                icon: ClipboardList,
              },
              {
                title: "Regulatory Compliance",
                desc: "Ensures full compliance with Canadian food safety and export regulations.",
                icon: CheckCircle,
              },
            ].map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <IconComponent className="w-10 h-10 text-red-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* Product Testing and Compliance */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Product Testing and Labeling Requirements
          </h2>
          <div className="bg-gradient-to-r from-red-50 to-orange-50 p-8 rounded-lg">
            <p className="text-lg text-gray-700 mb-6">
              CFIA compliance includes comprehensive product testing and
              labeling:
            </p>
            <ul className="space-y-3">
              {[
                "Microbiological Testing verifying food safety standards",
                "Chemical Analysis ensuring no harmful substances present",
                "Nutritional Labeling with accurate nutrition facts",
                "Allergen Disclosure listing all potential allergens",
                "Ingredient Declaration with complete ingredient listing",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </section>

      {/* Importance */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Importance of CFIA Compliance for Businesses
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Market Access to Canadian and export markets",
              "Regulatory Compliance meeting all CFIA requirements",
              "Consumer Confidence ensuring food safety",
              "Competitive Advantage in the food market",
              "Supply Chain Integrity maintaining food safety",
              "Legal Protection through compliance documentation",
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4"
              >
                <Star className="w-6 h-6 text-red-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Significance */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Significance of CFIA Compliance
          </h2>
          <p className="text-lg text-gray-700 mb-6">
            CFIA compliance organizations:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Access Canadian Market with food products",
              "Maintain Food Safety Standards consistently",
              "Comply with Export Requirements for other markets",
              "Build Trust with retailers and consumers",
              "Reduce Food Safety Risks in supply chain",
              "Meet Government Regulatory Standards",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-red-100 to-orange-100 p-6 rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-red-600 mb-3" />
                <p className="text-gray-800 font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Advantages */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Advantages of CFIA Compliance
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Market Access to Canada and export opportunities",
              "Regulatory Compliance with all food safety laws",
              "Product Safety ensuring customer protection",
              "Enhanced Reputation with consumers and retailers",
              "Operational Efficiency in food production",
              "Risk Reduction through proper documentation",
              "Export Credentials enabling international trade",
              "Consumer Trust and brand loyalty",
            ].map((advantage, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 p-4 bg-white rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Industries That Require CFIA Compliance
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Food Manufacturers & Processors",
              "Food Importers & Exporters",
              "Meat & Poultry Processing",
              "Dairy and Vegetable Processing",
              "Beverage Manufacturing",
              "Food Distributors & Retailers",
            ].map((industry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg"
              >
                <Factory className="w-5 h-5 text-red-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{industry}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How to Get Certified */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            How to Achieve CFIA Compliance
          </h2>
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: "Facility Registration",
                desc: "Register your facility with CFIA for inspection and approval",
              },
              {
                step: 2,
                title: "HACCP Implementation",
                desc: "Develop and implement HACCP plans for all food products",
              },
              {
                step: 3,
                title: "Product Testing",
                desc: "Conduct microbiological and chemical testing of products",
              },
              {
                step: 4,
                title: "Labeling Requirements",
                desc: "Ensure complete and accurate product labeling with all information",
              },
              {
                step: 5,
                title: "Documentation",
                desc: "Maintain comprehensive records and traceability documentation",
              },
              {
                step: 6,
                title: "CFIA Facility Inspection",
                desc: "Undergo CFIA inspection to verify compliance",
              },
              {
                step: 7,
                title: "Approval & Compliance",
                desc: "Receive CFIA approval and maintain ongoing compliance",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-r from-red-50 via-orange-50 to-yellow-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900 text-center">
            Why Choose MBA Solutions for CFIA Compliance?
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              MBA Solutions Pvt. Ltd. provides comprehensive CFIA compliance
              services, helping food businesses navigate Canadian food safety
              regulations and ensure successful market entry and operations in
              Canada.
            </p>
            <ul className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "HACCP Plan Development for Canadian Requirements",
                "Facility Registration Assistance with CFIA",
                "Product Testing Coordination",
                "Labeling Compliance Review",
                "Record Keeping and Traceability Setup",
                "CFIA Inspection Preparation",
              ].map((service, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{service}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 mb-6">
              We assist food businesses in achieving and maintaining CFIA
              compliance for Canadian market access and export operations.
            </p>
            <div className="border-t pt-6 mt-6">
              <p className="text-gray-800 font-semibold mb-4">Get in Touch:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-red-600" />
                  <span className="text-gray-700">
                    <strong>Email:</strong> atif@mbasolutions.co
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-red-600" />
                  <span className="text-gray-700">
                    <strong>Website:</strong> www.mbasolutions.co
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </>
  );
};

export default CFIA_Details;
